import React, {useCallback, useEffect, useRef, useState} from 'react'
import {GoogleMap, useJsApiLoader} from '@react-google-maps/api'
import useDealers from '../hooks/useDealers'
import useImporters from '../hooks/useImporters'
import useSearch from '../hooks/useSearch'
import CustomMarker from './components/CustomMarker'
import CountrySelector from '../components/CountrySelector'
import Search from '../components/Search'
import centerMapOnMarkers from '../utils/centerMapOnMarkers'
import findClosestMarkers from '../utils/findClosestMarkers'
import mapStyle from './mapStyle.json'
import useCountryStore from '../stores/useCountryStore'
import useSearchStore from '../stores/useSearchStore'

const containerStyle = {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
    background: '#232a25'
}

const center = {
    lat: 55.3990011183602,
    lng: 10.363338686609422
}

const mapOptions = {
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: false,
    styles: mapStyle
}

const DealersMap = ({title, selectPlaceholder, searchPlaceholder, searchLabel, selectLabel, language, searchErrorMessage}) => {
    const [map, setMap] = useState(null)
    const [initialDealers, setInitialDealers] = useState(null)
    const [initialImporters, setInitialImporters] = useState(null)
    const {selectedCountry} = useCountryStore()
    const {searchString} = useSearchStore()
    const {location, locationLoading, locationError} = useSearch(selectedCountry, searchString)
    const {dealers, dealersLoading, dealersError} = useDealers(selectedCountry, '')
    const {importers, importersLoading, importersError} = useImporters(selectedCountry, '')
    const {isLoaded, loadError} = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyCPsCnErzxwbzx_aMHH1oeH062JlWLL4GQ"
        // googleMapsApiKey: "AIzaSyCagIW816KbkuuxtXEwEKnHl6PDZH3wOqw"
    })

    const onLoad = useCallback(
        function onLoad(mapInstance) {
            setMap(mapInstance)
        }, []
    )

    /**
     * Display error if map can't load
     */
    if (loadError) {
        return <div>Map cannot be loaded right now.</div>
    }

    /**
     * Set initial dealers
     */
    if (!dealersLoading && !initialDealers) {
        setInitialDealers(dealers)
    }

    /**
     * Set initial importers
     */
    if (!importersLoading && !initialImporters) {
        setInitialImporters(importers)
    }

    /**
     * Center on markers if there's no search value
     */
    if (!dealersLoading && !importersLoading && searchString.length < 3 && map) {
        if (!selectedCountry) {
            map.setCenter(new google.maps.LatLng(55.352155, 10.306986))
            map.setZoom(4)
        } else {
            const dealersAndImporters = [...dealers, ...importers]
            centerMapOnMarkers(dealersAndImporters, location, google, map)
        }
    }

    /**
     * Find markers closest to search
     */
    if (location && !locationLoading && searchString.length > 2) {
        if (!dealers && !importers) return

        const allMarkers = [...dealers, ...importers]
        const closestMarkers = findClosestMarkers(allMarkers, location)

        centerMapOnMarkers(closestMarkers, location, google, map)
    }

    return isLoaded ? (
        <>
            <div className='page_container'>
                <div className='mtt-dealers-map__box'>
                    {title && <h1 className='title--3'>{title}</h1>}
                    <div className='mtt-dealers-map__inputs'>
                        <div className='mtt-dealers-map__input'>
                            <label>{selectLabel}</label>
                            <CountrySelector selectPlaceholder={selectPlaceholder} language={language}/>
                        </div>
                        <div className='mtt-dealers-map__input'>
                            <label>{searchLabel}</label>
                            <Search searchPlaceholder={searchPlaceholder} disabled={!selectedCountry}/>
                            {locationError && <div className='mtt-dealers-map__input-error'>{searchErrorMessage}</div>}
                        </div>
                    </div>
                </div>
            </div>
            <div className='mtt-dealers-map__map'>
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    onLoad={onLoad}
                    zoom={9}
                    options={mapOptions}
                    clickableIcons={false}
                >
                    {
                        initialDealers
                            ? initialDealers.map((dealer, key) =>
                                <CustomMarker
                                    key={key}
                                    dealer={dealer}
                                    type='dealer'
                                />
                            ) : null
                    }
                    {
                        initialImporters
                            ? initialImporters.map((importer, key) =>
                                <CustomMarker
                                    key={key}
                                    dealer={importer}
                                    type='importer'
                                />
                            ) : null
                    }
                </GoogleMap>
            </div>
        </>
    ) : null
}

export default DealersMap